import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useBooking } from '../../contexts/BookingContext';
import { generateTimeSlots, getTodayDate, formatDateShort, addDays, formatPrice } from '../../utils/timeUtils';
import { RichTextEditor } from '../RichTextEditor';
import { Calendar } from '../Calendar';
import { ConfirmationModal } from '../ConfirmationModal';
import { ReservationDetailModal } from '../ReservationDetailModal';
import { GroupBookingModal } from './GroupBookingModal';
import { PaymentMethodModal } from './PaymentMethodModal';
import { RescheduleModal } from './RescheduleModal';
import type { Booking, Facility } from '../../types';
import type { AvailableTrainer } from '../../types/trainerAssociation';

// Booking modal for single facility booking
const BookingModalPlaceholder: React.FC<{
  facilityId: string;
  facilityName: string;
  facilityPricePerHour?: number;
  date: string;
  startHour: number;
  endHour: number;
  onClose: () => void;
}> = ({ facilityId, facilityName, facilityPricePerHour, date, startHour, endHour, onClose }) => {
  const { t } = useTranslation();
  const { createBooking, facilityTimeSlots, fetchAvailableTrainers } = useBooking();
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [editedStartHour, setEditedStartHour] = useState(startHour);
  const [editedEndHour, setEditedEndHour] = useState(endHour);
  const [customPrice, setCustomPrice] = useState<string>('');
  const [hasManualPrice, setHasManualPrice] = useState(false);
  const [notes, setNotes] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Trainer state
  const [availableTrainers, setAvailableTrainers] = useState<AvailableTrainer[]>([]);
  const [loadingTrainers, setLoadingTrainers] = useState(false);
  const [selectedTrainerId, setSelectedTrainerId] = useState<string | null>(null);

  const timeSlots = generateTimeSlots(6, 22);

  // Get selected trainer's price
  const selectedTrainer = availableTrainers.find((t: any) =>
    (t.trainerProfileId || t.id) === selectedTrainerId
  ) as any;
  const trainerPricePerHour = selectedTrainer?.grossHourlyRate || selectedTrainer?.hourlyRate || selectedTrainer?.pricePerHour || 0;
  const hours = editedEndHour - editedStartHour;
  const facilityPrice = (facilityPricePerHour || 0) * hours;
  const trainerPrice = trainerPricePerHour * hours;
  const totalPrice = facilityPrice + trainerPrice;

  // Update custom price when total price changes (but only if not manually set)
  useEffect(() => {
    if (!hasManualPrice) {
      setCustomPrice(totalPrice > 0 ? formatPrice(totalPrice) : '');
    }
  }, [totalPrice, hasManualPrice]);

  const isSlotAvailable = (hour: number): boolean => {
    const slots = facilityTimeSlots[facilityId]?.[date] || [];
    const time = `${hour.toString().padStart(2, '0')}:00`;
    const status = slots.find(s => s.time === time);
    return (status?.isAvailable === true) && (status?.isBooked === false);
  };

  // Fetch available trainers when time changes
  useEffect(() => {
    const fetchTrainers = async () => {
      if (editedStartHour >= editedEndHour) return;

      const timeSlotsToCheck: string[] = [];
      for (let hour = editedStartHour; hour < editedEndHour; hour++) {
        timeSlotsToCheck.push(`${hour.toString().padStart(2, '0')}:00`);
      }

      setLoadingTrainers(true);
      try {
        const trainers = await fetchAvailableTrainers(date, timeSlotsToCheck);
        setAvailableTrainers(trainers);
        // Reset trainer selection if currently selected trainer is no longer available
        if (selectedTrainerId && !trainers.find((t: any) => (t.trainerProfileId || t.id) === selectedTrainerId)) {
          setSelectedTrainerId(null);
        }
      } catch (err) {
        console.error('Failed to fetch trainers:', err);
        setAvailableTrainers([]);
      } finally {
        setLoadingTrainers(false);
      }
    };

    fetchTrainers();
  }, [date, editedStartHour, editedEndHour, fetchAvailableTrainers]);

  const handleSubmit = async () => {
    if (!customerName.trim()) {
      setError(t('scheduler.modals.enterCustomerName'));
      return;
    }

    // Validate all slots are available
    for (let hour = editedStartHour; hour < editedEndHour; hour++) {
      if (!isSlotAvailable(hour)) {
        setError(t('booking.timeSlotNotAvailable'));
        return;
      }
    }

    setIsCreating(true);
    setError(null);

    try {
      await createBooking({
        facilityId,
        timeSlot: {
          startTime: `${editedStartHour.toString().padStart(2, '0')}:00`,
          endTime: `${editedEndHour.toString().padStart(2, '0')}:00`,
          date,
        },
        customerName: customerName.trim(),
        customerPhone: customerPhone.trim() || undefined,
        customerEmail: customerEmail.trim() || undefined,
        isPaid: false,
        notes: notes.trim() || undefined,
        customPrice: customPrice ? parseFloat(customPrice) : undefined,
        trainerProfileId: selectedTrainerId,
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('booking.createBookingError'));
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-brand-primary-deep rounded-lg shadow-xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
        <h3 className="text-xl font-bold text-white mb-2">{t('scheduler.modals.createBooking')}</h3>
        <p className="text-sm text-white/70 mb-4">{facilityName} - {formatDateShort(date)}</p>

        {error && (
          <div className="mb-4 p-3 bg-brand-warm/20 border border-brand-warm/40 rounded-lg">
            <p className="text-sm text-brand-warm">{error}</p>
          </div>
        )}

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-white/70 mb-1">{t('booking.startTime')}</label>
              <select
                value={editedStartHour}
                onChange={(e) => setEditedStartHour(parseInt(e.target.value))}
                className="w-full px-3 py-2 bg-surface-card border border-border-strong rounded-lg text-ink-primary"
              >
                {timeSlots.map((time, i) => (
                  <option key={time} value={6 + i}>{time}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-white/70 mb-1">{t('booking.endTime')}</label>
              <select
                value={editedEndHour}
                onChange={(e) => setEditedEndHour(parseInt(e.target.value))}
                className="w-full px-3 py-2 bg-surface-card border border-border-strong rounded-lg text-ink-primary"
              >
                {timeSlots.map((time, i) => (
                  <option key={time} value={6 + i}>{time}</option>
                ))}
                <option value={22}>22:00</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-white/70 mb-1">{t('scheduler.modals.customerNameRequired')}</label>
            <input
              type="text"
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              className="w-full px-3 py-2 bg-surface-card border border-border-strong rounded-lg text-ink-primary"
              placeholder={t('booking.customerNamePlaceholder')}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-white/70 mb-1">{t('scheduler.modals.phoneNumber')}</label>
            <input
              type="tel"
              value={customerPhone}
              onChange={(e) => setCustomerPhone(e.target.value)}
              className="w-full px-3 py-2 bg-surface-card border border-border-strong rounded-lg text-ink-primary"
              placeholder={t('booking.phoneNumberPlaceholder')}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-white/70 mb-1">{t('scheduler.modals.email')}</label>
            <input
              type="email"
              value={customerEmail}
              onChange={(e) => setCustomerEmail(e.target.value)}
              className="w-full px-3 py-2 bg-surface-card border border-border-strong rounded-lg text-ink-primary"
              placeholder={t('booking.emailPlaceholder')}
            />
          </div>

          {/* Trainer Selection */}
          <div>
            <label className="block text-sm font-medium text-white/70 mb-1">{t('groupBooking.trainer')}</label>
            <select
              value={selectedTrainerId || ''}
              onChange={(e) => setSelectedTrainerId(e.target.value || null)}
              disabled={loadingTrainers}
              className="w-full px-3 py-2 bg-surface-card border border-border-strong rounded-lg text-ink-primary disabled:opacity-50"
            >
              <option value="">{t('groupBooking.noTrainer')}</option>
              {loadingTrainers ? (
                <option disabled>{t('groupBooking.loadingTrainers')}</option>
              ) : (
                availableTrainers.map((trainer: any) => {
                  const trainerPrice = trainer.grossHourlyRate || trainer.hourlyRate || trainer.pricePerHour;
                  return (
                    <option key={trainer.trainerProfileId || trainer.id} value={trainer.trainerProfileId || trainer.id}>
                      {trainer.trainerDisplayName || trainer.displayName || trainer.name}
                      {trainerPrice ? ` (+${formatPrice(trainerPrice)} PLN/h)` : ''}
                    </option>
                  );
                })
              )}
            </select>
            {!loadingTrainers && availableTrainers.length === 0 && (
              <p className="text-xs text-white/60 mt-1">{t('groupBooking.noTrainersAvailable')}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-white/70 mb-1">
              {t('scheduler.modals.customPrice')} ({t('scheduler.modals.defaultPriceLabel')} {formatPrice(totalPrice)} PLN
              {trainerPrice > 0 && <span className="text-xs text-white/60"> {t('groupBooking.includesTrainer')}</span>})
            </label>
            <input
              type="number"
              value={customPrice}
              onChange={(e) => {
                setCustomPrice(e.target.value);
                setHasManualPrice(true);
              }}
              className="w-full px-3 py-2 bg-surface-card border border-border-strong rounded-lg text-ink-primary"
              placeholder={formatPrice(totalPrice)}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-white/70 mb-1">{t('scheduler.modals.notes')}</label>
            <RichTextEditor
              value={notes}
              onChange={setNotes}
              placeholder={t('scheduler.modals.optionalNotes')}
              minRows={2}
            />
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <button
            onClick={onClose}
            disabled={isCreating}
            className="flex-1 px-4 py-2 bg-white/10 border border-white/30 text-white rounded-lg hover:bg-white/20 transition-colors disabled:opacity-50"
          >
            {t('scheduler.modals.cancel')}
          </button>
          <button
            onClick={handleSubmit}
            disabled={isCreating}
            className="flex-1 px-4 py-2 bg-brand-primary text-white rounded-lg hover:bg-brand-primary-hover font-semibold disabled:opacity-50"
          >
            {isCreating ? t('scheduler.modals.creating') : t('scheduler.modals.createBooking')}
          </button>
        </div>
      </div>
    </div>
  );
};

// Znajduje realnie przewijalny kontener nad danym elementem. W tym portalu są dwa
// zagnieżdżone `overflow-auto` (MainLayout + siatka), a scrolluje ten, który ma
// nadmiar treści — dlatego wybieramy go dynamicznie, a nie na sztywno.
function findScrollableParent(el: HTMLElement | null): HTMLElement | null {
  let node = el?.parentElement ?? null;
  while (node) {
    const overflowY = getComputedStyle(node).overflowY;
    if ((overflowY === 'auto' || overflowY === 'scroll') && node.scrollHeight > node.clientHeight + 1) {
      return node;
    }
    node = node.parentElement;
  }
  return null;
}

interface SchedulerAllFacilitiesProps {
  hiddenFacilityIds?: Set<string>;
}

export const SchedulerAllFacilities: React.FC<SchedulerAllFacilitiesProps> = ({ hiddenFacilityIds }) => {
  const { t } = useTranslation();
  const {
    facilities: allFacilities,
    fetchAllFacilitiesBookings,
    facilityTimeSlots,
    bookings,
    deleteBooking,
    getReservationSlots,
    cancelReservationSlots,
    toggleReservationPayment,
    applyProductToReservation,
    rescheduleReservation,
    updateReservationNotes,
  } = useBooking();

  const facilities = useMemo(
    () => (hiddenFacilityIds && hiddenFacilityIds.size > 0
      ? allFacilities.filter(f => !hiddenFacilityIds.has(f.id))
      : allFacilities),
    [allFacilities, hiddenFacilityIds]
  );

  const [currentDate, setCurrentDate] = useState(getTodayDate());
  const [showCalendar, setShowCalendar] = useState(false);
  const [showGroupBookingModal, setShowGroupBookingModal] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState<{ bookingId: string; customerName: string } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [reservationDetailModal, setReservationDetailModal] = useState<string | null>(null);
  const [bookingModal, setBookingModal] = useState<{
    facilityId: string;
    facilityName: string;
    facilityPricePerHour?: number;
    startHour: number;
    endHour: number;
  } | null>(null);
  const [paymentModal, setPaymentModal] = useState<{ reservationId: string; customerEmail: string } | null>(null);
  const [rescheduleModal, setRescheduleModal] = useState<{
    reservationId: string;
    currentDate: string;
    currentTimeSlots: string[];
    currentFacilityId: string;
    customerName: string;
  } | null>(null);

  // Drag selection state
  const [isDragging, setIsDragging] = useState(false);
  const [dragFacility, setDragFacility] = useState<Facility | null>(null);
  const [dragStart, setDragStart] = useState<number | null>(null);
  const [dragEnd, setDragEnd] = useState<number | null>(null);

  const timeSlots = generateTimeSlots(6, 22);

  // Pierwsza otwarta godzina w skali całej siatki: najwcześniejszy wiersz, w którym
  // KTÓRAKOLWIEK oś jest otwarta (dostępna albo zajęta, byle nie „niedostępna").
  // Do niego przewijamy widok, żeby domyślnie zaczynał się od godzin pracy obiektu,
  // a nie od 6:00. Wcześniejsze godziny zostają dostępne po przewinięciu w górę.
  const firstOpenTime = timeSlots.find(time =>
    facilities.some(facility => {
      const slot = (facilityTimeSlots[facility.id]?.[currentDate] || []).find(s => s.time === time);
      return !!slot && (slot.isAvailable || slot.isBooked);
    })
  ) ?? null;
  const firstOpenRowRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const headerRowRef = useRef<HTMLDivElement>(null);

  // Fetch bookings for all facilities when date changes
  useEffect(() => {
    fetchAllFacilitiesBookings(currentDate).catch(console.error);
  }, [currentDate, fetchAllFacilitiesBookings]);

  // Domyślnie ustaw widok na pierwszą otwartą godzinę. Liczymy pozycję wiersza z
  // getBoundingClientRect i ustawiamy scrollTop NATYCHMIAST (bez smooth — pozycja
  // startowa; płynny scroll bywa porzucany po odświeżeniu). Odejmujemy wysokość
  // sticky-nagłówka, żeby godzina nie schowała się pod nim. Kilka prób w rAF, bo
  // dane slotów po refreshu dochodzą asynchronicznie.
  useEffect(() => {
    if (!firstOpenTime) return;
    let cancelled = false;
    let attempts = 0;
    let rafId = 0;
    const tryScroll = () => {
      if (cancelled) return;
      const row = firstOpenRowRef.current;
      const preferred = scrollContainerRef.current;
      const scroller =
        preferred && preferred.scrollHeight > preferred.clientHeight + 1
          ? preferred
          : findScrollableParent(row);
      if (row && scroller) {
        const headerH = headerRowRef.current?.offsetHeight ?? 44;
        const delta = row.getBoundingClientRect().top - scroller.getBoundingClientRect().top - headerH;
        if (Math.abs(delta) > 2) scroller.scrollTop += delta;
      } else if (row) {
        row.scrollIntoView({ block: 'start', behavior: 'auto' });
      }
      attempts += 1;
      if (attempts < 10) rafId = requestAnimationFrame(tryScroll);
    };
    rafId = requestAnimationFrame(tryScroll);
    return () => {
      cancelled = true;
      cancelAnimationFrame(rafId);
    };
  }, [firstOpenTime, currentDate]);

  const handlePrevious = () => {
    setCurrentDate(addDays(currentDate, -1));
  };

  const handleNext = () => {
    setCurrentDate(addDays(currentDate, 1));
  };

  const handleToday = () => {
    setCurrentDate(getTodayDate());
  };

  const getSlotStatus = (facilityId: string, time: string) => {
    const slots = facilityTimeSlots[facilityId]?.[currentDate] || [];
    return slots.find(s => s.time === time);
  };

  const getBookingForSlot = (facilityId: string, time: string): Booking | undefined => {
    return bookings.find((booking) => {
      if (booking.facilityId !== facilityId || booking.timeSlot.date !== currentDate) {
        return false;
      }
      const startHour = parseInt(booking.timeSlot.startTime.split(':')[0]);
      const slotHour = parseInt(time.split(':')[0]);
      const endHour = parseInt(booking.timeSlot.endTime.split(':')[0]);
      return slotHour >= startHour && slotHour < endHour;
    });
  };

  const isBookingStart = (booking: Booking, time: string): boolean => {
    const startHour = parseInt(booking.timeSlot.startTime.split(':')[0]);
    const slotHour = parseInt(time.split(':')[0]);
    return slotHour === startHour;
  };

  const getBookingHeight = (booking: Booking): number => {
    const startHour = parseInt(booking.timeSlot.startTime.split(':')[0]);
    const endHour = parseInt(booking.timeSlot.endTime.split(':')[0]);
    return endHour - startHour;
  };

  const handleSlotClick = (facility: Facility, time: string) => {
    if (isDragging) return;

    const slotStatus = getSlotStatus(facility.id, time);
    const booking = getBookingForSlot(facility.id, time);

    if (!booking && slotStatus?.isAvailable && !slotStatus.isBooked) {
      setShowGroupBookingModal(true);
    }
  };

  const handleBookingClick = (booking: Booking, e: React.MouseEvent) => {
    e.stopPropagation();
    const reservationId = booking.id.substring(0, booking.id.lastIndexOf('-'));
    setReservationDetailModal(reservationId);
  };

  const handleMouseDown = (facility: Facility, hour: number) => {
    const time = `${hour.toString().padStart(2, '0')}:00`;
    const slotStatus = getSlotStatus(facility.id, time);
    const booking = getBookingForSlot(facility.id, time);

    if (!booking && slotStatus?.isAvailable && !slotStatus.isBooked) {
      setIsDragging(true);
      setDragFacility(facility);
      setDragStart(hour);
      setDragEnd(hour);
    }
  };

  const handleMouseEnter = (facility: Facility, hour: number) => {
    if (isDragging && dragFacility && dragFacility.id === facility.id) {
      const time = `${hour.toString().padStart(2, '0')}:00`;
      const slotStatus = getSlotStatus(facility.id, time);
      const booking = getBookingForSlot(facility.id, time);

      if (!booking && slotStatus?.isAvailable && !slotStatus.isBooked) {
        setDragEnd(hour);
      }
    }
  };

  const handleMouseUp = useCallback(() => {
    if (isDragging && dragFacility && dragStart !== null && dragEnd !== null) {
      const startHour = Math.min(dragStart, dragEnd);
      const endHour = Math.max(dragStart, dragEnd) + 1;

      // Check all slots are available
      let allAvailable = true;
      for (let hour = startHour; hour < endHour; hour++) {
        const time = `${hour.toString().padStart(2, '0')}:00`;
        const slotStatus = getSlotStatus(dragFacility.id, time);
        const booking = getBookingForSlot(dragFacility.id, time);
        if (booking || !slotStatus?.isAvailable || slotStatus.isBooked) {
          allAvailable = false;
          break;
        }
      }

      if (allAvailable) {
        setShowGroupBookingModal(true);
      }
    }

    setIsDragging(false);
    setDragFacility(null);
    setDragStart(null);
    setDragEnd(null);
  }, [isDragging, dragFacility, dragStart, dragEnd, facilityTimeSlots, bookings, currentDate]);

  useEffect(() => {
    const handleGlobalMouseUp = () => handleMouseUp();
    window.addEventListener('mouseup', handleGlobalMouseUp);
    return () => window.removeEventListener('mouseup', handleGlobalMouseUp);
  }, [handleMouseUp]);

  const isSlotInDragSelection = (facilityId: string, hour: number): boolean => {
    if (!isDragging || !dragFacility || dragFacility.id !== facilityId || dragStart === null || dragEnd === null) {
      return false;
    }
    const minHour = Math.min(dragStart, dragEnd);
    const maxHour = Math.max(dragStart, dragEnd);
    return hour >= minHour && hour <= maxHour;
  };

  const handleDeleteClick = async () => {
    if (!deleteConfirmation) return;

    setIsDeleting(true);
    setCancelError(null);

    try {
      await deleteBooking(deleteConfirmation.bookingId);
      setDeleteConfirmation(null);
    } catch (err) {
      if (err instanceof Error && err.message.includes('same-day')) {
        setCancelError(t('errors.cannotCancelSameDayReservations'));
      } else {
        setCancelError(err instanceof Error ? err.message : t('errors.generalError'));
      }
    } finally {
      setIsDeleting(false);
    }
  };

  const isToday = currentDate === getTodayDate();

  return (
    <div className="flex-1 flex flex-col h-full overflow-hidden bg-surface-app">
      {/* Header */}
      <div className="px-6 pt-4 pb-3 border-b border-border-subtle bg-surface-card">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button
              onClick={handlePrevious}
              className="px-3 py-1.5 text-sm bg-surface-app-elevated text-ink-primary rounded-lg hover:bg-surface-card-hover transition-colors"
            >
              {t('scheduler.previous')}
            </button>
            <button
              onClick={handleToday}
              className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                isToday
                  ? 'bg-brand-primary text-white'
                  : 'bg-surface-app-elevated text-ink-primary hover:bg-surface-card-hover'
              }`}
            >
              {t('scheduler.today')}
            </button>
            <button
              onClick={handleNext}
              className="px-3 py-1.5 text-sm bg-surface-app-elevated text-ink-primary rounded-lg hover:bg-surface-card-hover transition-colors"
            >
              {t('scheduler.next')}
            </button>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowCalendar(true)}
              className="px-3 py-1.5 text-sm bg-surface-app-elevated text-ink-primary rounded-lg hover:bg-surface-card-hover transition-colors flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              {formatDateShort(currentDate)}
            </button>

            <button
              onClick={() => setShowGroupBookingModal(true)}
              className="px-4 py-1.5 text-sm bg-brand-primary text-white rounded-lg hover:bg-brand-primary-hover transition-colors font-medium flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              {t('allFacilities.groupBooking')}
            </button>
          </div>
        </div>
      </div>

      {/* Main Grid */}
      <div ref={scrollContainerRef} className="flex-1 overflow-auto">
        <div className="min-w-max">
          {/* Header Row with Facility Names */}
          <div ref={headerRowRef} className="flex sticky top-0 z-20 bg-surface-card border-b-2 border-border-strong">
            <div className="w-20 flex-shrink-0 border-r border-border-subtle bg-surface-card" aria-label={t('allFacilities.time')} />
            {facilities.map(facility => (
              <div
                key={facility.id}
                className="flex-1 min-w-[112px] px-2 py-2.5 text-center border-r border-border-subtle"
                title={facility.name}
              >
                <div className="font-semibold text-sm text-ink-primary truncate">
                  {facility.name}
                </div>
              </div>
            ))}
          </div>

          {/* Time Slots Grid */}
          {timeSlots.map((time, timeIndex) => {
            const hour = 6 + timeIndex;
            const isFirstOpen = time === firstOpenTime;
            return (
              <div
                key={time}
                ref={isFirstOpen ? firstOpenRowRef : null}
                className="flex border-b border-border-subtle"
              >
                {/* Time Label */}
                <div className="w-20 flex-shrink-0 flex items-center justify-center border-r border-border-subtle bg-surface-app-elevated py-4">
                  <span className="text-sm font-medium text-ink-secondary">{time}</span>
                </div>

                {/* Facility Columns */}
                {facilities.map(facility => {
                  const slotStatus = getSlotStatus(facility.id, time);
                  const booking = getBookingForSlot(facility.id, time);
                  const isInDragSelection = isSlotInDragSelection(facility.id, hour);

                  // Determine slot background color
                  let bgColor = 'bg-surface-app-elevated/30 dark:bg-surface-app-elevated/60'; // default not available
                  if (slotStatus?.isAvailable && !slotStatus.isBooked) {
                    bgColor = 'bg-brand-confirm/15 dark:bg-brand-confirm/30 hover:bg-brand-confirm/25 dark:hover:bg-brand-confirm/45 cursor-pointer';
                  } else if (slotStatus?.businessProfileId) {
                    bgColor = 'bg-brand-primary-light/40 dark:bg-brand-primary-light/55';
                  }

                  if (isInDragSelection) {
                    bgColor = 'bg-brand-primary/15 dark:bg-brand-primary/30 ring-2 ring-brand-primary ring-inset';
                  }

                  return (
                    <div
                      key={facility.id}
                      className={`flex-1 min-w-[112px] h-[60px] p-2 border-r border-border-subtle relative ${bgColor} transition-colors select-none`}
                      onClick={() => handleSlotClick(facility, time)}
                      onMouseDown={() => handleMouseDown(facility, hour)}
                      onMouseEnter={() => handleMouseEnter(facility, hour)}
                    >
                      {/* Booking Card */}
                      {booking && isBookingStart(booking, time) && (
                        <div
                          className={`absolute inset-2 rounded-lg p-2 shadow-lg overflow-hidden cursor-pointer ${
                            booking.isPaid
                              ? 'bg-brand-confirm hover:brightness-110'
                              : 'bg-brand-accent hover:brightness-110'
                          }`}
                          style={{
                            height: `${getBookingHeight(booking) * 60 - 8}px`,
                            zIndex: 10,
                          }}
                          onClick={(e) => handleBookingClick(booking, e)}
                        >
                          {booking.groupId && (
                            <div className="absolute top-0 left-0 right-0 h-[3px] bg-brand-primary-light" aria-hidden />
                          )}
                          <div className="flex flex-col h-full">
                            {/* Action buttons row — top */}
                            <div className="flex flex-shrink-0 justify-between items-center">
                              <div className="flex gap-0.5 items-center">
                                {!booking.paymentId && !booking.isPaid && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const lastHyphenIndex = booking.id.lastIndexOf('-');
                                      const reservationId = lastHyphenIndex !== -1 ? booking.id.substring(0, lastHyphenIndex) : booking.id;
                                      setPaymentModal({
                                        reservationId,
                                        customerEmail: booking.customerEmail || '',
                                      });
                                    }}
                                    className="px-1.5 py-0.5 bg-white/20 hover:bg-white/30 rounded text-xs text-white transition-colors"
                                    title={t('buttons.markAsPaid')}
                                  >
                                    $
                                  </button>
                                )}
                                {booking.paymentId && (
                                  <span className="px-1.5 py-0.5 bg-white/25 rounded text-xs text-white" title={t('buttons.markAsPaid')}>
                                    💳
                                  </span>
                                )}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const lastHyphenIndex = booking.id.lastIndexOf('-');
                                    const reservationId = lastHyphenIndex !== -1 ? booking.id.substring(0, lastHyphenIndex) : booking.id;
                                    const startHour = parseInt(booking.timeSlot.startTime.split(':')[0]);
                                    const endHour = parseInt(booking.timeSlot.endTime.split(':')[0]);
                                    const slots: string[] = [];
                                    for (let h = startHour; h < endHour; h++) {
                                      slots.push(`${h.toString().padStart(2, '0')}:00`);
                                    }
                                    setRescheduleModal({
                                      reservationId,
                                      currentDate: booking.timeSlot.date,
                                      currentTimeSlots: slots,
                                      currentFacilityId: booking.facilityId,
                                      customerName: booking.customerName,
                                    });
                                  }}
                                  className="px-1.5 py-0.5 bg-white/25 hover:bg-white/40 rounded text-xs text-white transition-colors"
                                  title={t('buttons.reschedule') || 'Reschedule'}
                                >
                                  R
                                </button>
                              </div>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDeleteConfirmation({
                                    bookingId: booking.id,
                                    customerName: booking.customerName,
                                  });
                                }}
                                className="px-1.5 py-0.5 bg-white/25 hover:bg-white/40 rounded text-xs text-white transition-colors"
                                title={t('buttons.deleteBooking')}
                              >
                                ×
                              </button>
                            </div>
                            {/* Customer info — below buttons */}
                            <div className="font-semibold text-white text-xs truncate flex items-center gap-1 mt-1">
                              {booking.groupId && (
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-3 h-3 flex-shrink-0 text-white/90" aria-label="Group booking">
                                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                                  <circle cx="9" cy="7" r="4" />
                                  <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                                </svg>
                              )}
                              <span className="truncate">{booking.customerName}</span>
                            </div>
                            {booking.customerPhone && getBookingHeight(booking) > 1.5 && (
                              <div className="text-xs text-white/75 mt-1 truncate">
                                {booking.customerPhone}
                              </div>
                            )}
                            {booking.notes && getBookingHeight(booking) > 2 && (
                              <div className="text-xs text-white/75 mt-1 border-t border-white/20 pt-1 truncate">
                                {booking.notes}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>

      {/* Legend */}
      <div className="bg-surface-card border-t border-border-subtle p-4">
        <div className="flex flex-wrap items-center gap-6 text-sm">
          <div className="font-semibold text-ink-primary">{t('scheduler.legend.slotStatus')}</div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-brand-confirm/15 dark:bg-brand-confirm/30"></div>
            <span className="text-ink-secondary">{t('scheduler.legend.available')}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-surface-app-elevated"></div>
            <span className="text-ink-secondary">{t('scheduler.legend.notAvailable')}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-brand-primary-light/40 dark:bg-brand-primary-light/55 border-l-2 border-brand-primary-light"></div>
            <span className="text-ink-secondary">{t('scheduler.legend.businessSlot')}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-brand-confirm"></div>
            <span className="text-ink-secondary">{t('scheduler.legend.paid')}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-brand-accent"></div>
            <span className="text-ink-secondary">{t('scheduler.legend.unpaid')}</span>
          </div>
        </div>
      </div>

      {/* Modals */}
      <Calendar
        isOpen={showCalendar}
        selectedDate={currentDate}
        onDateSelect={(date) => {
          setCurrentDate(date);
          setShowCalendar(false);
        }}
        onClose={() => setShowCalendar(false)}
      />

      {bookingModal && (
        <BookingModalPlaceholder
          facilityId={bookingModal.facilityId}
          facilityName={bookingModal.facilityName}
          facilityPricePerHour={bookingModal.facilityPricePerHour}
          date={currentDate}
          startHour={bookingModal.startHour}
          endHour={bookingModal.endHour}
          onClose={() => setBookingModal(null)}
        />
      )}

      {showGroupBookingModal && (
        <GroupBookingModal
          isOpen={showGroupBookingModal}
          onClose={() => setShowGroupBookingModal(false)}
          defaultDate={currentDate}
        />
      )}

      <ConfirmationModal
        isOpen={!!deleteConfirmation}
        title={t('scheduler.confirmation.cancelBooking')}
        message={`${t('scheduler.confirmation.confirmCancelBookingMessage')} ${deleteConfirmation?.customerName || ''}?`}
        confirmLabel={isDeleting ? t('scheduler.confirmation.cancelling') : t('scheduler.confirmation.cancelBookingButton')}
        cancelLabel={t('common.cancel')}
        onConfirm={handleDeleteClick}
        onCancel={() => {
          setDeleteConfirmation(null);
          setCancelError(null);
        }}
        errorMessage={cancelError}
        confirmVariant="danger"
        showNotesField={true}
        notesLabel={t('scheduler.confirmation.cancellationReason')}
        notesPlaceholder={t('scheduler.confirmation.cancellationPlaceholder')}
      />

      <ReservationDetailModal
        isOpen={!!reservationDetailModal}
        reservationId={reservationDetailModal}
        onClose={() => setReservationDetailModal(null)}
        onCancelComplete={() => {
          fetchAllFacilitiesBookings(currentDate).catch(console.error);
        }}
        onFetchReservation={getReservationSlots}
        onCancelSlots={cancelReservationSlots}
        onTogglePayment={toggleReservationPayment}
        onUpdateNotes={updateReservationNotes}
        onReschedule={(reservation) => {
          const activeSlots = reservation.slots
            .filter(s => s.status === 'Active')
            .map(s => s.timeSlot);
          setReservationDetailModal(null);
          setRescheduleModal({
            reservationId: reservation.id,
            currentDate: reservation.date.substring(0, 10),
            currentTimeSlots: activeSlots,
            currentFacilityId: reservation.facilityId,
            customerName: reservation.customerName || '',
          });
        }}
      />

      <PaymentMethodModal
        isOpen={paymentModal !== null}
        reservationId={paymentModal?.reservationId || ''}
        customerEmail={paymentModal?.customerEmail || ''}
        onClose={() => setPaymentModal(null)}
        onPayWithCash={async (reservationId) => {
          await toggleReservationPayment(reservationId);
          setPaymentModal(null);
          fetchAllFacilitiesBookings(currentDate).catch(console.error);
        }}
        onPayWithProduct={async (reservationId, purchaseIdPrefix, userEmail) => {
          await applyProductToReservation(reservationId, { purchaseIdPrefix, userEmail });
          setPaymentModal(null);
          fetchAllFacilitiesBookings(currentDate).catch(console.error);
        }}
      />

      <RescheduleModal
        isOpen={rescheduleModal !== null}
        reservationId={rescheduleModal?.reservationId || ''}
        currentDate={rescheduleModal?.currentDate || ''}
        currentTimeSlots={rescheduleModal?.currentTimeSlots || []}
        currentFacilityId={rescheduleModal?.currentFacilityId || ''}
        customerName={rescheduleModal?.customerName || ''}
        onClose={() => setRescheduleModal(null)}
        onReschedule={async (reservationId, data) => {
          await rescheduleReservation(reservationId, data);
          setRescheduleModal(null);
          fetchAllFacilitiesBookings(currentDate).catch(console.error);
        }}
      />
    </div>
  );
};
